import { NineSliceContainer } from "@ravalmatic/catafractum-utils";
import { GameVars } from "../../GameVars";
import { PersistentDataManager } from "../../PersistentDataManager";
import { GameData, GameType } from "../../Types";

type SettingKey = "gameType" | "humanDeclares" | "showLastHand" | "playableCardHints";

export class SettingsLayer extends Phaser.GameObjects.Container {

    private static readonly WIDTH = 740;
    private static readonly HEIGHT = 470;
    private static readonly PORTRAIT_WIDTH = SettingsLayer.WIDTH - 20;
    private static readonly PORTRAIT_HEIGHT = SettingsLayer.HEIGHT - 110;
    private static readonly LEFT_BORDER_SIZE = 138;
    private static readonly RIGHT_BORDER_SIZE = 138;
    private static readonly TOP_BORDER_SIZE = 106;
    private static readonly BOTTOM_BORDER_SIZE = 105;
    private static readonly ROW_WIDTH = 652;
    private static readonly ROW_HEIGHT = 76;
    private static readonly ROW_SPACING = 102;
    private static readonly TEXT_COLOR = "#19364D";

    private readonly refreshRows: (() => void)[] = [];
    private readonly background: NineSliceContainer;

    constructor(scene: Phaser.Scene) {

        super(scene, 0, 175);

        this.background = new NineSliceContainer(
            this.scene, 0, 20, "texture_atlas_1", "bg_settings",
            GameVars.isLandscape ? SettingsLayer.WIDTH : SettingsLayer.PORTRAIT_WIDTH,
            GameVars.isLandscape ? SettingsLayer.HEIGHT : SettingsLayer.PORTRAIT_HEIGHT,
            SettingsLayer.LEFT_BORDER_SIZE, SettingsLayer.RIGHT_BORDER_SIZE,
            SettingsLayer.TOP_BORDER_SIZE, SettingsLayer.BOTTOM_BORDER_SIZE
        );
        this.setGameScaleY(GameVars.scaleY);
        this.add(this.background);

        const rows = [
            this.createRow(
                "gameType", "icon_settings_game_type", "GAME TYPE",
                ["CHICAGO", "RUBBER"], [GameType.CHICAGO, GameType.RUBBER]
            ),
            this.createRow(
                "humanDeclares", "icon_settings_human_declares", "HUMAN DECLARES",
                ["ON", "OFF"], [true, false]
            ),
            this.createRow(
                "showLastHand", "icon_settings_show_last_trick", "SHOW LAST TRICK",
                ["ON", "OFF"], [true, false]
            ),
            this.createRow(
                "playableCardHints", "icon_settings_playable_card_hints", "PLAYABLE CARD HINTS",
                ["ON", "OFF"], [true, false]
            )
        ];
        rows.forEach((row, index) => {
            row.setY((index - (rows.length - 1) / 2) * SettingsLayer.ROW_SPACING + 14);
            this.add(row);
        });
    }

    public refresh(): void {

        this.refreshRows.forEach(refreshRow => refreshRow());
    }

    public setGameScaleY(scaleY: number): void {

        if (!Number.isFinite(scaleY) || scaleY <= 0) {
            throw new Error(`Settings layer requires a positive vertical scale; received ${scaleY}`);
        }
        const width = GameVars.isLandscape ? SettingsLayer.WIDTH : SettingsLayer.PORTRAIT_WIDTH;
        const height = GameVars.isLandscape ? SettingsLayer.HEIGHT : SettingsLayer.PORTRAIT_HEIGHT;
        this.background.setPanelSize(width, height);
        this.background.setScale(1, 1 / scaleY);
        this.background.setInteractive({
            hitArea: new Phaser.Geom.Rectangle(
                -width / 2, -height / (2 * scaleY), width, height / scaleY
            ),
            hitAreaCallback: Phaser.Geom.Rectangle.Contains
        });
    }

    private createRow<TKey extends SettingKey>(
        key: TKey, iconFrame: string, label: string, options: readonly [string, string],
        values: readonly [GameData[TKey], GameData[TKey]]
    ): Phaser.GameObjects.Container {

        const row = new Phaser.GameObjects.Container(this.scene);
        const background = new Phaser.GameObjects.Graphics(this.scene);
        background.fillStyle(0xfff5e6, .35);
        background.lineStyle(2, 0xe5d1b5, 1);
        const x = -SettingsLayer.ROW_WIDTH / 2;
        const y = -SettingsLayer.ROW_HEIGHT / 2;
        background.fillRoundedRect(x, y, SettingsLayer.ROW_WIDTH, SettingsLayer.ROW_HEIGHT, 18);
        background.strokeRoundedRect(x, y, SettingsLayer.ROW_WIDTH, SettingsLayer.ROW_HEIGHT, 18);

        const iconY = key === "gameType" ? 9 : key === "showLastHand" ? 8 : 0;
        const icon = new Phaser.GameObjects.Image(this.scene, -278, iconY, "texture_atlas_1", iconFrame);
        icon.setScale(.7);
        const title = new Phaser.GameObjects.Text(this.scene, -224, 0, label, {
            fontFamily: "Baloo2-Bold", fontSize: "22px", color: SettingsLayer.TEXT_COLOR
        }).setOrigin(0, .5);

        const switchImage = new Phaser.GameObjects.Image(
            this.scene, 216, 0, "texture_atlas_1", "btn_switch_left_on"
        );
        switchImage.setDisplaySize(212, 50);
        switchImage.setInteractive({ useHandCursor: true });
        const optionLabels = options.map((option, index) => new Phaser.GameObjects.Text(
            this.scene,
            switchImage.x + switchImage.displayWidth * (index === 0 ? -.21 : .28),
            0,
            option,
            {
                fontFamily: "Baloo2-Bold", fontSize: "20px",
                color: index === 0 ? "#FFFFFF" : SettingsLayer.TEXT_COLOR
            }
        ).setOrigin(.5));
        const refresh = () => {
            const leftSelected = GameVars.gameData[key] === values[0];
            switchImage.setFrame(leftSelected ? "btn_switch_left_on" : "btn_switch_left_off");
            switchImage.setDisplaySize(212, 50);
            optionLabels[0].setX(switchImage.x + switchImage.displayWidth * (leftSelected ? -.21 : -.28));
            optionLabels[1].setX(switchImage.x + switchImage.displayWidth * (leftSelected ? .28 : .21));
            optionLabels[0].setColor(leftSelected ? "#FFFFFF" : SettingsLayer.TEXT_COLOR);
            optionLabels[1].setColor(leftSelected ? SettingsLayer.TEXT_COLOR : "#FFFFFF");
        };
        switchImage.on("pointerdown", (_pointer: Phaser.Input.Pointer, localX: number) => {
            if (!this.visible || !switchImage.input?.enabled) {
                return;
            }
            const value = values[localX < switchImage.width / 2 ? 0 : 1];
            if (GameVars.gameData[key] === value) {
                return;
            }
            PersistentDataManager.updateGameData(key, value);
            refresh();
        });
        this.refreshRows.push(refresh);
        refresh();
        row.add([background, icon, title, switchImage, ...optionLabels]);
        return row;
    }
}
